import { Loader2, Search } from "lucide-react"
import { useEffect, useRef, useState } from "react"

import { Input } from "@/components/ui/input"
import { useDebounce } from "@/hooks/use-debounce"
import { cn } from "@/lib/utils"

/**
 * Generalised debounced search input.
 *
 * Originally extracted from OKS-17's `CandidateSearchInput` (now a thin
 * wrapper) so OKS-25 (crew roster) and any future list-page can reuse
 * the same UX:
 *   - Controlled by `defaultValue`; component owns local typing state
 *   - Debounces the caller's `onDebouncedChange` (default 300ms)
 *   - Leading icon swaps `Search → Loader2` while debounce pending
 *     (`data-state="pending"|"idle"` exposes for tests)
 *   - First-render guard: never fires `onDebouncedChange` for the seed
 *     value (would cause a useless re-fetch on mount)
 *
 * Caller supplies the locked `testId`, accessible label, and placeholder
 * — i18n strings live in the caller, not here.
 *
 * Wire-format note (Crew memory `vitest_wire_format_canary`): callers
 * that mount this inside an Inertia `<Form>` should pass `name` to
 * surface the wire key on the underlying `<input>` for Vitest literal
 * `name="..."` asserts. OKS-25 uses URL-only filters (no `<Form>`) so
 * `name` is optional.
 */
interface DebouncedSearchInputProps {
  defaultValue: string
  onDebouncedChange: (value: string) => void
  testId: string
  ariaLabel: string
  placeholder?: string
  /** Used inside Inertia `<Form>` for wire-format binding. Optional. */
  name?: string
  delayMs?: number
  className?: string
}

export function DebouncedSearchInput({
  defaultValue,
  onDebouncedChange,
  testId,
  ariaLabel,
  placeholder,
  name,
  delayMs = 300,
  className,
}: DebouncedSearchInputProps) {
  const [value, setValue] = useState(defaultValue)
  const debounced = useDebounce(value, delayMs)

  // First-render guard: don't fire onDebouncedChange for the seed value
  // (it'd cause a useless URL rewrite to the same state on mount).
  const seeded = useRef(false)

  useEffect(() => {
    if (!seeded.current) {
      seeded.current = true
      return
    }
    onDebouncedChange(debounced)
    // We intentionally exclude onDebouncedChange from deps to avoid
    // re-firing when the parent's callback identity changes; the parent
    // is responsible for stable handler refs (or accepting the effect
    // re-fire if it doesn't).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced])

  const isPending = value !== debounced

  return (
    <div className={cn("relative", className)}>
      <span
        aria-hidden="true"
        data-state={isPending ? "pending" : "idle"}
        className="text-muted-foreground absolute top-1/2 left-3 -translate-y-1/2"
      >
        {isPending ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <Search className="size-4" />
        )}
      </span>
      <Input
        type="search"
        inputMode="search"
        autoComplete="off"
        spellCheck={false}
        name={name}
        data-testid={testId}
        aria-label={ariaLabel}
        placeholder={placeholder}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        className="pl-9"
      />
    </div>
  )
}
